<template>
  <div class="personal-page">
    <aside class="personal-sidebar" aria-label="Sezioni area personale">
      <div class="personal-sidebar-hero">
        <div class="personal-avatar" aria-hidden="true">
          <i class="fa-solid fa-user"></i>
        </div>
        <div class="personal-identity">
          <h3>{{ personalDisplayName }}</h3>
          <p>{{ utente?.email || "Caricamento..." }}</p>
        </div>
      </div>

      <div class="personal-sidebar-divider"></div>

      <nav
        class="personal-sidebar-navigation"
        aria-label="Sezioni area personale"
      >
        <button
          type="button"
          class="personal-sidebar-nav-item"
          :class="{ active: activePersonalPanel === 'overview' }"
          :aria-current="activePersonalPanel === 'overview' ? 'page' : null"
          @click="selectPersonalPanel('overview')"
        >
          <i class="fa-solid fa-house"></i>
          <span>Panoramica</span>
        </button>
        <button
          type="button"
          class="personal-sidebar-nav-item"
          :class="{ active: activePersonalPanel === 'activity' }"
          :aria-current="activePersonalPanel === 'activity' ? 'page' : null"
          @click="selectPersonalPanel('activity')"
        >
          <i class="fa-solid fa-clipboard-list"></i>
          <span>Attività</span>
        </button>
        <button
          type="button"
          class="personal-sidebar-nav-item"
          :class="{ active: activePersonalPanel === 'account' }"
          :aria-current="activePersonalPanel === 'account' ? 'page' : null"
          @click="selectPersonalPanel('account')"
        >
          <i class="fa-solid fa-user-gear"></i>
          <span>Account</span>
        </button>
      </nav>
    </aside>

    <section class="personal-main" aria-label="Area personale">
      <header
        class="area-page-header area-page-header--personal area-page-header--section"
        aria-labelledby="personal-section-title"
      >
        <span
          class="area-page-header__icon area-page-header__icon--section"
          aria-hidden="true"
        >
          <i :class="activePersonalPanelMeta.icon"></i>
        </span>
        <div class="area-page-header__copy">
          <h1 id="personal-section-title" class="area-page-header__title">
            {{ activePersonalPanelMeta.title }}
          </h1>
          <p class="area-page-header__subtitle">
            {{ activePersonalPanelMeta.subtitle }}
          </p>
        </div>
        <img
          class="area-page-header__visual area-page-header__visual--personal"
          src="/Titoli pagine/Immagine Area personale.png"
          alt=""
          aria-hidden="true"
        />
      </header>

      <div class="personal-workspace">
        <div class="personal-panel-viewport">
          <section
            v-show="activePersonalPanel === 'overview'"
            class="personal-panel overview-panel"
          >
            <div class="panel-heading">
              <h3>I tuoi numeri</h3>
            </div>

            <div v-if="isLoading" class="personal-metrics loading-metrics">
              <div
                v-for="item in 4"
                :key="item"
                class="metric-card skeleton-card"
              ></div>
            </div>
            <div v-else class="personal-metrics">
              <article
                class="metric-card metric-card--converter metric-card--prospetti"
              >
                <span class="metric-icon" aria-hidden="true">
                  <ProductIcon product="prospetti" />
                </span>
                <div>
                  <span>Prospetti Paghe convertiti</span>
                  <strong>{{
                    utente?.conversioni_effettuate_prospetti || 0
                  }}</strong>
                </div>
              </article>
              <article
                class="metric-card metric-card--converter metric-card--f24"
              >
                <span class="metric-icon" aria-hidden="true">
                  <ProductIcon product="f24" />
                </span>
                <div>
                  <span>Modelli F24 convertiti</span>
                  <strong>{{ utente?.conversioni_effettuate_f24 || 0 }}</strong>
                </div>
              </article>
              <article
                class="metric-card metric-card--converter metric-card--bilanci"
              >
                <span class="metric-icon" aria-hidden="true">
                  <ProductIcon product="bilancio" />
                </span>
                <div>
                  <span>Bilanci Analitici convertiti</span>
                  <strong>{{
                    utente?.conversioni_effettuate_bilanci || 0
                  }}</strong>
                </div>
              </article>
              <button
                type="button"
                class="metric-card metric-card-link"
                title="Vai ai documenti convertiti"
                @click="selectPersonalPanel('activity')"
              >
                <span
                  class="metric-icon aggregate-metric-icon"
                  aria-hidden="true"
                >
                  <span class="aggregate-tool-icons">
                    <ProductIcon product="prospetti" />
                    <ProductIcon product="f24" />
                    <ProductIcon product="bilancio" />
                  </span>
                  <i class="fa-solid fa-folder-open aggregate-folder"></i>
                </span>
                <div>
                  <span>Documenti convertiti</span>
                  <strong>{{ convertedDocumentsTotal }}</strong>
                </div>
              </button>
            </div>

            <div class="personal-libraries">
              <div class="panel-heading personal-libraries-heading">
                <h3>Librerie</h3>
              </div>
              <div class="personal-library-grid">
                <router-link
                  to="/home/libreria-codici-conto"
                  class="personal-library-card personal-library-link"
                >
                  <span class="personal-library-image" aria-hidden="true">
                    <LibraryIcon
                      class="personal-library-image-emphasis"
                      library="conti"
                    />
                  </span>
                  <span class="personal-library-copy">
                    <span class="personal-library-scope is-personal">
                      <i class="fa-solid fa-user" aria-hidden="true"></i>
                      Personale
                    </span>
                    <strong>Codici Conto</strong>
                    <small>Apri libreria</small>
                  </span>
                  <i
                    class="fa-solid fa-chevron-right personal-library-chevron"
                    aria-hidden="true"
                  ></i>
                </router-link>

                <router-link
                  to="/home/libreria-codici-tributo"
                  class="personal-library-card personal-library-link"
                >
                  <span class="personal-library-image" aria-hidden="true">
                    <LibraryIcon
                      class="personal-library-image-emphasis"
                      library="tributi"
                    />
                  </span>
                  <span class="personal-library-copy">
                    <span class="personal-library-scope is-personal">
                      <i class="fa-solid fa-user" aria-hidden="true"></i>
                      Personale
                    </span>
                    <strong>Codici Tributo</strong>
                    <small>Apri libreria</small>
                  </span>
                  <i
                    class="fa-solid fa-chevron-right personal-library-chevron"
                    aria-hidden="true"
                  ></i>
                </router-link>

                <router-link
                  to="/home/libreria-bilanci-analitici"
                  class="personal-library-card personal-library-link"
                >
                  <span class="personal-library-image" aria-hidden="true">
                    <LibraryIcon library="bilanci" />
                  </span>
                  <span class="personal-library-copy">
                    <span class="personal-library-scope is-shared">
                      <i class="fa-solid fa-users" aria-hidden="true"></i>
                      Condivisa con il tuo studio
                    </span>
                    <strong>Bilanci Analitici</strong>
                    <small>Apri libreria</small>
                  </span>
                  <i
                    class="fa-solid fa-chevron-right personal-library-chevron"
                    aria-hidden="true"
                  ></i>
                </router-link>
              </div>
            </div>
          </section>

          <section
            v-show="activePersonalPanel === 'activity'"
            class="personal-panel activity-panel"
          >
            <div class="panel-heading activity-heading">
              <div>
                <h3>Le tue operazioni</h3>
                <p>
                  Cronologia delle conversioni effettuate con il tuo account.
                </p>
              </div>
              <span
                v-if="ha(user, 'cronologia_operazioni')"
                class="history-plan-chip"
              >
                <i class="fa-solid fa-clock-rotate-left"></i>
                {{
                  ha(user, "cronologia_illimitata") ? "Completa" : "Ultime 10"
                }}
              </span>
            </div>

            <p v-if="archivedFilesMessage" class="archived-files-message">
              {{ archivedFilesMessage }}
            </p>

            <div class="activity-table-shell">
              <table class="download-log-table">
                <thead>
                  <tr>
                    <th>Strumento</th>
                    <th>File Caricato</th>
                    <th>File Elaborato</th>
                    <th>Data Operazione</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-if="isLoading">
                    <tr v-for="n in 5" :key="`loading-${n}`">
                      <td v-for="cell in 4" :key="cell">
                        <div class="skeleton-cell"></div>
                      </td>
                    </tr>
                  </template>
                  <tr v-else-if="!ha(user, 'cronologia_operazioni')">
                    <td colspan="4" class="operationtext">
                      <i class="fa-solid fa-lock"></i>
                      Cronologia disponibile a partire dal piano Plus.
                    </td>
                  </tr>
                  <tr v-else-if="downloadLogs.length === 0">
                    <td colspan="4" class="operationtext">
                      Nessuna operazione effettuata finora.
                    </td>
                  </tr>
                  <template v-else>
                    <template v-for="log in downloadLogs">
                      <tr
                        :key="log.id"
                        :class="{ 'row-expanded': isExpanded(log.id) }"
                      >
                        <td class="activity-tool-cell">
                          <OperationToolLabel :operation="log.operazione" />
                        </td>
                        <td>
                          <button
                            v-if="hasUploadedArchivedFile(log)"
                            :class="[
                              'archived-file-link',
                              { disabled: !canDownloadArchivedFiles() },
                            ]"
                            @click.stop="downloadUploadedArchivedFile(log)"
                          >
                            {{ getUploadedArchivedFileLabel(log) }}
                          </button>
                          <template v-else>{{ log.file_name }}</template>
                        </td>
                        <td
                          class="processed-files-cell"
                          :class="{
                            'clickable-cell':
                              (log.file_scaricati &&
                                log.file_scaricati.length > 1) ||
                              hasArchivedFiles(log),
                          }"
                          @click="
                            log.file_scaricati && log.file_scaricati.length > 1
                              ? toggleExpand(log.id)
                              : null
                          "
                        >
                          <template v-if="Array.isArray(log.file_scaricati)">
                            <template v-if="log.file_scaricati.length === 1">
                              <button
                                v-if="hasArchivedFiles(log)"
                                :class="[
                                  'archived-file-link',
                                  { disabled: !canDownloadArchivedFiles() },
                                ]"
                                @click.stop="downloadArchivedFile(log, 0)"
                              >
                                {{ getArchivedFileLabel(log, 0) }}
                              </button>
                              <template v-else>{{
                                log.file_scaricati[0]
                              }}</template>
                            </template>
                            <span
                              v-else-if="log.file_scaricati.length > 1"
                              class="chip"
                            >
                              <i class="fa-solid fa-folder"></i>
                              {{ log.file_scaricati.length }} file
                            </span>
                            <template v-else>Nessun file scaricato</template>
                          </template>
                          <template v-else>Nessun file scaricato</template>
                        </td>
                        <td>{{ formatDate(log.download_date) }}</td>
                      </tr>
                      <tr
                        v-if="isExpanded(log.id)"
                        :key="`expanded-${log.id}`"
                        class="expanded-log-row"
                      >
                        <td colspan="4" class="expanded-row">
                          <span class="filescaricatititle"
                            >File scaricati:</span
                          >
                          <ul>
                            <li
                              v-for="(file, index) in log.file_scaricati || []"
                              :key="`${log.id}-${file}-${index}`"
                            >
                              <button
                                v-if="hasArchivedFileAt(log, index)"
                                :class="[
                                  'archived-file-link',
                                  { disabled: !canDownloadArchivedFiles() },
                                ]"
                                @click="downloadArchivedFile(log, index)"
                              >
                                {{ getArchivedFileLabel(log, index) }}
                              </button>
                              <template v-else>{{ file }}</template>
                            </li>
                          </ul>
                        </td>
                      </tr>
                    </template>
                  </template>
                </tbody>
              </table>
            </div>
          </section>

          <section
            v-show="activePersonalPanel === 'account'"
            class="personal-panel account-panel"
          >
            <div class="panel-heading account-heading">
              <div>
                <h3>Account</h3>
                <p>Aggiorna i tuoi dati personali e le opzioni di accesso.</p>
              </div>
              <span class="account-member-since">
                <i class="fa-regular fa-calendar" aria-hidden="true"></i>
                Membro dal {{ registrationDate }}
              </span>
            </div>

            <div class="account-sections">
              <article class="account-card">
                <div class="account-card-heading">
                  <i class="fa-solid fa-user-pen"></i>
                  <div>
                    <h4>Nome e cognome</h4>
                    <p>Modifica i dati visualizzati nel tuo profilo.</p>
                  </div>
                </div>
                <form
                  class="account-form profile-form"
                  @submit.prevent="updateProfile"
                >
                  <label aria-label="Nome">
                    <input
                      v-model="form.firstName"
                      type="text"
                      placeholder="Nome"
                      autocomplete="given-name"
                      required
                    />
                  </label>
                  <label aria-label="Cognome">
                    <input
                      v-model="form.lastName"
                      type="text"
                      placeholder="Cognome"
                      autocomplete="family-name"
                      required
                    />
                  </label>
                  <div class="account-form-actions">
                    <button
                      type="submit"
                      class="primary-action"
                      :disabled="!canSaveProfile"
                    >
                      {{
                        isUpdatingProfile ? "Salvataggio..." : "Salva modifiche"
                      }}
                    </button>
                  </div>
                </form>
                <p
                  v-if="profileMessage"
                  class="form-message"
                  :class="profileMessageType"
                >
                  {{ profileMessage }}
                </p>
              </article>

              <article class="account-card">
                <div class="account-card-heading">
                  <i class="fa-solid fa-key"></i>
                  <div>
                    <h4>Password</h4>
                    <p>
                      Gestisci la password utilizzata per accedere a MyStudio.
                    </p>
                  </div>
                </div>
                <form
                  v-if="canChangePasswordInMyStudio"
                  class="account-form password-form"
                  @submit.prevent="cambiaPassword"
                >
                  <label>
                    <span>Password attuale</span>
                    <input
                      v-model="form.vecchiaPassword"
                      type="password"
                      required
                    />
                  </label>
                  <label>
                    <span>Nuova password</span>
                    <input
                      v-model="form.nuovaPassword"
                      type="password"
                      minlength="6"
                      required
                    />
                  </label>
                  <label>
                    <span>Conferma password</span>
                    <input
                      v-model="form.confermaPassword"
                      type="password"
                      minlength="6"
                      required
                    />
                  </label>
                  <div class="account-form-actions">
                    <button
                      type="submit"
                      class="primary-action"
                      :disabled="isChangingPassword"
                    >
                      {{
                        isChangingPassword
                          ? "Aggiornamento..."
                          : "Cambia password"
                      }}
                    </button>
                  </div>
                </form>
                <div
                  v-else-if="usesExternalPasswordManager"
                  class="external-account-action"
                >
                  <p>
                    La password è protetta dal gestore sicuro del tuo account.
                  </p>
                  <button
                    type="button"
                    class="primary-action"
                    @click="apriGestionePassword"
                  >
                    Gestisci password
                  </button>
                </div>
                <div v-else class="external-account-action disabled">
                  <p>
                    Il cambio password non è disponibile per questo account.
                  </p>
                </div>
                <p
                  v-if="passwordMessage"
                  class="form-message"
                  :class="passwordMessageType"
                >
                  {{ passwordMessage }}
                </p>
              </article>

              <article class="account-card danger-card">
                <div class="account-card-heading">
                  <i class="fa-solid fa-trash"></i>
                  <div>
                    <h4>Elimina account</h4>
                    <p>
                      Rimuove definitivamente il tuo account e i dati personali
                      associati.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  class="danger-action"
                  @click="openDeleteModal"
                >
                  Elimina account
                </button>
              </article>
            </div>
          </section>
        </div>
      </div>
    </section>

    <div
      v-if="mostraModalDelete"
      class="personal-modal"
      @click.self="mostraModalDelete = false"
    >
      <div
        class="personal-modal-card danger-modal"
        role="alertdialog"
        aria-modal="true"
      >
        <div class="modal-icon">
          <i class="fa-solid fa-triangle-exclamation"></i>
        </div>
        <h3>Eliminare il tuo account?</h3>
        <p>
          Questa azione è irreversibile e cancellerà tutti i dati personali
          associati.
        </p>
        <div class="modal-actions">
          <button
            class="danger-action"
            :disabled="isDeleting"
            @click="deleteAccount"
          >
            {{ isDeleting ? "Eliminazione..." : "Conferma eliminazione" }}
          </button>
          <button class="secondary-action" @click="mostraModalDelete = false">
            Annulla
          </button>
        </div>
        <p v-if="deleteMessage" class="form-message" :class="deleteMessageType">
          {{ deleteMessage }}
        </p>
      </div>
    </div>

    <div
      v-if="mostraClerkProfileModal"
      class="personal-modal"
      @click.self="chiudiGestionePasswordClerk"
    >
      <div class="clerk-profile-modal-content">
        <button
          type="button"
          class="clerk-profile-close"
          aria-label="Chiudi gestione account"
          @click="chiudiGestionePasswordClerk"
        >
          &times;
        </button>
        <div ref="clerkUserProfileContainer" class="clerk-profile-host"></div>
      </div>
    </div>
  </div>
</template>

<script>
import AreaPersonale from "./AreaPersonale.vue";
import ProductIcon from "@/components/ProductIcon.vue";
import LibraryIcon from "@/components/LibraryIcon.vue";
import OperationToolLabel from "@/components/OperationToolLabel.vue";

export default {
  name: "AreaPersonaleDashboard",
  extends: AreaPersonale,
  components: {
    ProductIcon,
    LibraryIcon,
    OperationToolLabel,
  },
  data() {
    return {
      activePersonalPanel: "overview",
    };
  },
  computed: {
    activePersonalPanelMeta() {
      const panels = {
        overview: {
          title: "Panoramica",
          subtitle:
            "Una vista completa dei tuoi numeri, delle librerie e del tuo profilo.",
          icon: "fa-solid fa-house",
        },
        activity: {
          title: "Attività",
          subtitle:
            "Consulta le conversioni e le operazioni effettuate con il tuo account.",
          icon: "fa-solid fa-clipboard-list",
        },
        account: {
          title: "Account",
          subtitle: "Gestisci i tuoi dati personali e le opzioni di accesso.",
          icon: "fa-solid fa-user-gear",
        },
      };

      return panels[this.activePersonalPanel] || panels.overview;
    },
    personalDisplayName() {
      const name = [this.utente?.first_name, this.utente?.last_name]
        .filter(Boolean)
        .join(" ")
        .trim();
      return name || "Completa il tuo profilo";
    },
    registrationDate() {
      return this.utente?.data_registrazione
        ? this.formatDate(this.utente.data_registrazione)
        : "—";
    },
    convertedDocumentsTotal() {
      return (
        Number(this.utente?.conversioni_effettuate_prospetti || 0) +
        Number(this.utente?.conversioni_effettuate_f24 || 0) +
        Number(this.utente?.conversioni_effettuate_bilanci || 0)
      );
    },
    canSaveProfile() {
      const firstName = String(this.form.firstName || "").trim();
      const lastName = String(this.form.lastName || "").trim();
      const savedFirstName = String(this.utente?.first_name || "").trim();
      const savedLastName = String(this.utente?.last_name || "").trim();

      return (
        !this.isUpdatingProfile &&
        Boolean(firstName) &&
        Boolean(lastName) &&
        (firstName !== savedFirstName || lastName !== savedLastName)
      );
    },
  },
  methods: {
    selectPersonalPanel(panel) {
      this.activePersonalPanel = panel;
    },
  },
};
</script>

<style scoped>
.personal-page {
  --personal-left-offset: 2rem;
  --personal-right-offset: 2rem;
  --personal-navbar-reserved-height: 92px;
  --personal-sidebar-width: 16.75rem;
  flex: 1 1 0;
  display: grid;
  grid-template-columns: var(--personal-sidebar-width) minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr);
  align-items: stretch;
  justify-content: start;
  column-gap: 1rem;
  row-gap: 0;
  box-sizing: border-box;
  width: 100%;
  height: calc(100dvh - var(--personal-navbar-reserved-height));
  max-height: calc(100dvh - var(--personal-navbar-reserved-height));
  min-height: 0;
  margin-inline: 0;
  padding: 1.1rem clamp(1rem, 5vw, 6rem) 1rem;
  overflow: hidden;
  background: #f7f9fb;
  color: #17243d;
}

.area-page-header {
  position: relative;
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 0.9rem;
  width: 100%;
  min-height: 5.85rem;
  padding: 0.72rem 13rem 0.72rem 1rem;
  overflow: hidden;
  box-sizing: border-box;
  border: 1px solid rgba(102, 123, 140, 0.14);
  border-radius: 12px;
  background: linear-gradient(105deg, #f7f9fa 0%, #eff3f6 68%, #e7edf2 100%);
  box-shadow: 0 7px 20px rgba(64, 83, 99, 0.06);
}

.area-page-header__icon {
  position: relative;
  z-index: 1;
  display: inline-flex;
  width: 3.7rem;
  height: 3.7rem;
  flex: 0 0 3.7rem;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: linear-gradient(145deg, #e8f6ef, #dcefe6);
  color: #07815e;
  font-size: 1.55rem;
}

.area-page-header__copy {
  position: relative;
  z-index: 1;
  min-width: 0;
  text-align: left;
}

.area-page-header__title {
  margin: 0;
  color: #15334b;
  font-size: 1.42rem;
  font-weight: 750;
  letter-spacing: -0.015em;
  line-height: 1.15;
}

.area-page-header__subtitle {
  margin: 0.3rem 0 0;
  color: #657c88;
  font-size: 0.82rem;
  line-height: 1.35;
}

.area-page-header__visual {
  position: absolute;
  z-index: 0;
  display: block;
  pointer-events: none;
  user-select: none;
}

.area-page-header__visual--personal {
  right: 1.15rem;
  bottom: -1.15rem;
  width: 12.4rem;
  opacity: 0.58;
}

.personal-sidebar {
  grid-column: 1;
  grid-row: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 0;
  padding: 1rem 0.5rem;
  overflow: hidden;
  box-sizing: border-box;
  border: 1px solid #dfe7ef;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 14px 34px rgba(15, 37, 55, 0.07);
}

.personal-sidebar-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.65rem;
  padding: 0.4rem 0.5rem 1.15rem;
  text-align: center;
}

.personal-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 5.8rem;
  height: 5.8rem;
  flex: 0 0 5.8rem;
  overflow: hidden;
  border-radius: 999px;
  background: radial-gradient(
    circle at 35% 25%,
    #fafbfc 0,
    #f1f3f5 58%,
    #e8ecef 100%
  );
  color: #253852;
  font-size: 2.25rem;
}

.personal-avatar img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.personal-identity {
  width: 100%;
  min-width: 0;
}

.personal-identity h3,
.personal-identity p {
  margin: 0;
}

.personal-identity h3 {
  color: #13243f;
  font-size: 1.12rem;
  line-height: 1.2;
}

.personal-identity p {
  margin-top: 0.35rem;
  overflow-wrap: anywhere;
  color: #52657a;
  font-size: 0.86rem;
}

.personal-sidebar-divider {
  height: 1px;
  margin: 0 0 0.55rem;
  background: #dfe7ef;
}

.personal-sidebar-navigation {
  display: flex;
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  flex-direction: column;
  gap: 0.25rem;
  box-sizing: border-box;
  padding: 0;
}

.personal-sidebar-nav-item {
  display: flex;
  width: 100%;
  min-height: 3rem;
  align-items: center;
  gap: 0.6rem;
  box-sizing: border-box;
  padding: 0.62rem 0.5rem;
  border: 0;
  border-left: 3px solid transparent;
  border-radius: 8px;
  background: transparent;
  color: #34495e;
  font: inherit;
  font-size: 0.88rem;
  font-weight: 650;
  line-height: 1.2;
  text-align: left;
  cursor: pointer;
  transition: color 0.18s ease, background 0.18s ease, border-color 0.18s ease;
}

.personal-sidebar-nav-item i {
  width: 1.25rem;
  flex: 0 0 1.25rem;
  color: #49677f;
  font-size: 1rem;
  text-align: center;
  transition: color 0.18s ease;
}

.personal-sidebar-nav-item span {
  min-width: 0;
  white-space: nowrap;
}

.personal-sidebar-nav-item:hover {
  background: #f2f7f5;
  color: #087552;
}

.personal-sidebar-nav-item:hover i,
.personal-sidebar-nav-item.active i {
  color: #07815e;
}

.personal-sidebar-nav-item.active {
  border-left-color: #07815e;
  background: linear-gradient(90deg, #e5f5ed 0%, #eef8f4 100%);
  color: #087552;
}

.personal-main {
  grid-column: 2;
  grid-row: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  gap: 0.75rem;
  overflow: hidden;
}

.personal-workspace {
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  width: 100%;
  height: 0;
  min-height: 0;
  overflow: hidden;
  box-sizing: border-box;
  border: 1px solid #dfe7ef;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 12px 30px rgba(15, 37, 55, 0.07);
}

.personal-panel-viewport {
  flex: 1 1 0;
  width: 100%;
  height: 0;
  min-height: 0;
  padding: 1rem 1.2rem 1.2rem;
  overflow: hidden;
  box-sizing: border-box;
}

.personal-panel {
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: auto;
  box-sizing: border-box;
  border: 1px solid #e0e6ed;
  border-radius: 12px;
  background: #fff;
}

.overview-panel,
.account-panel {
  padding: 1.35rem;
}

.overview-panel {
  overflow: hidden;
}

.panel-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.panel-heading h3,
.panel-heading p {
  margin: 0;
}

.panel-heading h3 {
  color: #102a43;
  font-size: 1.08rem;
}

.panel-heading p {
  margin-top: 0.25rem;
  color: #68798b;
  font-size: 0.84rem;
}

.personal-metrics {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.9rem;
}

.metric-card {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.75rem;
  height: 7.2rem;
  min-height: 7.2rem;
  padding: 0.85rem;
  box-sizing: border-box;
  border-radius: 8px;
  background: linear-gradient(145deg, #f8fafb, #f2f4f6);
  text-align: left;
}

.metric-card--converter {
  border: 1px solid var(--metric-border);
  background: linear-gradient(145deg, var(--metric-soft), #f8fafb 78%);
}

.metric-card--prospetti {
  --metric-accent: #16885f;
  --metric-strong: #076247;
  --metric-soft: rgba(22, 136, 95, 0.11);
  --metric-border: rgba(22, 136, 95, 0.16);
}

.metric-card--f24 {
  --metric-accent: #168bd2;
  --metric-strong: #075994;
  --metric-soft: rgba(22, 139, 210, 0.1);
  --metric-border: rgba(22, 139, 210, 0.16);
}

.metric-card--bilanci {
  --metric-accent: #b77909;
  --metric-strong: #795006;
  --metric-soft: rgba(183, 121, 9, 0.1);
  --metric-border: rgba(183, 121, 9, 0.16);
}

.metric-card--converter .metric-icon {
  display: inline-flex;
  width: 3rem;
  height: 3rem;
  align-items: center;
  justify-content: center;
  border-radius: 0.72rem;
  background: transparent;
  color: var(--metric-accent);
  box-shadow: none;
}

.metric-card--converter .metric-icon .product-icon {
  width: 88%;
  height: 88%;
  transform: scale(1.12);
}

.metric-card--converter strong {
  color: var(--metric-strong);
}

.metric-card--prospetti strong {
  color: #076247;
}

.metric-card--f24 strong {
  color: #075994;
}

.metric-card--bilanci strong {
  color: #795006;
}

.metric-card-link {
  border: 0;
  color: inherit;
  font: inherit;
  cursor: pointer;
  transition: background 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.metric-card-link:hover {
  background: linear-gradient(145deg, #f3f8f6, #eaf4ef);
  box-shadow: 0 7px 16px rgba(15, 71, 50, 0.1);
  transform: translateY(-1px);
}

.metric-card-link:focus-visible {
  outline: 3px solid rgba(11, 125, 85, 0.22);
  outline-offset: 2px;
}

.metric-icon {
  flex: 0 0 auto;
  color: #29425d;
  font-size: 2rem;
}

.aggregate-metric-icon {
  position: relative;
  display: inline-flex;
  width: 3.8rem;
  height: 3.35rem;
  align-items: flex-end;
  justify-content: center;
  overflow: visible;
}

.aggregate-folder {
  position: relative;
  z-index: 2;
  margin-bottom: 0.05rem;
  font-size: 2.15rem;
}

.aggregate-tool-icons {
  position: absolute;
  top: -0.18rem;
  left: 50%;
  z-index: 1;
  display: flex;
  align-items: flex-end;
  gap: 0.22rem;
  transform: translateX(-50%);
}

.aggregate-tool-icons .product-icon {
  width: 1.2rem;
  height: 1.2rem;
  border-radius: 3px;
  background: #f8fafb;
  box-shadow: 0 1px 3px rgba(24, 48, 72, 0.16);
}

.aggregate-tool-icons .product-icon:first-child {
  transform: translateY(0.3rem) rotate(-10deg);
}

.aggregate-tool-icons .product-icon:nth-child(2) {
  transform: translateY(-0.08rem);
}

.aggregate-tool-icons .product-icon:last-child {
  transform: translateY(0.3rem) rotate(10deg);
}

.metric-card > div {
  min-width: 0;
}

.metric-card > div > span {
  display: block;
  min-height: 0;
  color: #536477;
  font-size: 0.94rem;
  font-weight: 700;
  line-height: 1.2;
  white-space: normal;
}

.metric-card strong {
  display: block;
  margin-top: 0.35rem;
  color: #29425d;
  font-size: 1.55rem;
  font-weight: 600;
  line-height: 1;
}

.metric-card.metric-card--converter strong {
  color: var(--metric-strong);
}

.metric-card-link strong {
  color: #29425d;
}

.personal-libraries {
  margin-top: 1.2rem;
  padding-top: 1.2rem;
  border-top: 1px solid #e1e7ed;
}

.personal-libraries-heading {
  margin-bottom: 0.9rem;
}

.personal-library-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
}

.personal-library-card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
  height: 7.2rem;
  min-height: 7.2rem;
  padding: 0.8rem 0.85rem;
  box-sizing: border-box;
  border: 1px solid #e2e7ec;
  border-radius: 10px;
  background: #fbfcfd;
  color: #17243d;
  text-align: left;
  text-decoration: none;
}

.personal-library-image {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.65rem;
  height: 2.65rem;
  flex: 0 0 2.65rem;
  overflow: hidden;
  border-radius: 10px;
}

.personal-library-image img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  transform: scale(1);
}

.personal-library-image img.personal-library-image-emphasis {
  transform: scale(1.08);
}

.personal-library-image img.library-icon--bilanci {
  transform: scale(0.9);
}

.personal-library-copy {
  display: flex;
  min-width: 0;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 0.2rem;
  text-align: left;
}

.personal-library-copy strong {
  overflow: hidden;
  color: #17243d;
  font-size: 1.02rem;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.personal-library-scope {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 0.32rem;
  color: #68798b;
  font-size: 0.76rem;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
}

.personal-library-scope i {
  font-size: 0.7rem;
}

.personal-library-scope.is-personal {
  color: #176a9d;
}

.personal-library-scope.is-shared {
  color: #8a651d;
}

.personal-library-copy small {
  color: #68798b;
  font-size: 0.76rem;
  line-height: 1.25;
}

.personal-library-chevron {
  flex: 0 0 auto;
  color: #7d8b9b;
  font-size: 0.82rem;
  transition: transform 0.18s ease;
}

.personal-library-link {
  transition: border-color 0.18s ease, box-shadow 0.18s ease,
    transform 0.18s ease;
}

.personal-library-link:hover {
  border-color: #c3dacf;
  box-shadow: 0 8px 18px rgba(15, 71, 50, 0.09);
  transform: translateY(-1px);
}

.personal-library-link:hover .personal-library-chevron {
  transform: translateX(2px);
}

.skeleton-card,
.skeleton-cell {
  position: relative;
  overflow: hidden;
  background: #edf1f4;
}

.skeleton-card::after,
.skeleton-cell::after {
  content: "";
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.72),
    transparent
  );
  animation: personal-shimmer 1.4s infinite;
}

@keyframes personal-shimmer {
  100% {
    transform: translateX(100%);
  }
}

.activity-panel {
  display: flex;
  flex-direction: column;
  padding: 1.2rem;
  overflow: hidden;
}

.activity-heading {
  flex: 0 0 auto;
}

.activity-heading > div,
.account-heading > div {
  min-width: 0;
  flex: 1 1 auto;
  margin-left: 0.65rem;
  text-align: left;
}

.activity-heading :is(h3, p),
.account-heading :is(h3, p) {
  text-align: left;
}

.history-plan-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.65rem;
  border-radius: 999px;
  background: #eaf6ef;
  color: #0b7d55;
  font-size: 0.78rem;
  font-weight: 700;
}

.activity-table-shell {
  flex: 1 1 0;
  height: 0;
  min-height: 0;
  overflow: auto;
  border: 1px solid #e2e6ea;
  border-radius: 10px;
}

.download-log-table {
  width: 100%;
  min-width: 54rem;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
  font-size: 0.88rem;
}

.download-log-table th,
.download-log-table td {
  padding: 0.68rem 0.72rem;
  border-right: 1px solid #e2e6ea;
  border-bottom: 1px solid #e2e6ea;
  overflow-wrap: anywhere;
}

.download-log-table th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f3f4f6;
  color: #13243f;
  text-align: center;
}

.download-log-table td {
  background: #fff;
  text-align: left;
}

.download-log-table tbody tr:nth-child(even) > td {
  background: #f8f9fa;
}

.download-log-table th:nth-child(1),
.download-log-table td:nth-child(1) {
  width: 18%;
  text-align: left;
}

.download-log-table th:nth-child(2),
.download-log-table td:nth-child(2) {
  width: 25%;
}

.download-log-table th:nth-child(3),
.download-log-table td:nth-child(3) {
  width: 39%;
}

.download-log-table th:nth-child(4),
.download-log-table td:nth-child(4) {
  width: 18%;
  text-align: center;
}

.operationtext {
  height: 6rem;
  color: #68798b;
  text-align: center !important;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.28rem 0.55rem;
  border-radius: 999px;
  background: #eef1f5;
  color: #31455b;
}

.chip i {
  color: #e9aa1d;
}

.clickable-cell {
  cursor: pointer;
}

.download-log-table tbody tr.row-expanded > td {
  background: #f1f3f5;
}

.expanded-row {
  padding: 0.85rem 1rem !important;
  color: #607086;
  text-align: left !important;
}

.expanded-row .filescaricatititle {
  display: block;
  margin: 0 0 0.65rem;
  color: #4b6584;
  font-weight: 600;
  text-align: left;
}

.expanded-row ul {
  margin: 0;
  padding-left: 1.35rem;
  list-style-position: outside;
  text-align: left;
}

.expanded-row li,
.expanded-row .archived-file-link {
  text-align: left;
}

.expanded-row li {
  margin: 0.24rem 0;
  line-height: 1.25;
}

.archived-file-link {
  padding: 0;
  border: 0;
  background: transparent;
  color: #1d4ed8;
  font: inherit;
  text-decoration: underline;
  cursor: pointer;
}

.archived-file-link.disabled {
  color: #7b8794;
  text-decoration: none;
  cursor: not-allowed;
}

.archived-files-message {
  flex: 0 0 auto;
  margin: 0 0 0.7rem;
  color: #7c5c00;
  font-size: 0.85rem;
}

.settings-panel {
  min-height: 100%;
}

.account-heading {
  padding-bottom: 0.9rem;
  border-bottom: 1px solid #e1e7ed;
}

.account-member-since {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 0.45rem;
  margin-right: 0.65rem;
  color: #607286;
  font-size: 0.8rem;
  line-height: 1.2;
  white-space: nowrap;
}

.account-member-since i {
  color: #496b86;
  font-size: 0.9rem;
}

.account-sections {
  display: grid;
  grid-template-rows: repeat(3, 8rem);
  gap: 1rem;
}

.account-card {
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 1.1rem;
  box-sizing: border-box;
  border: 1px solid #e0e6ed;
  border-radius: 12px;
  background: #fbfcfd;
}

.account-card-heading {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.account-card-heading > div {
  min-width: 0;
  flex: 1 1 auto;
  text-align: left;
}

.account-card-heading > i {
  width: 2rem;
  height: 2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 2rem;
  border-radius: 8px;
  background: #eaf6ef;
  color: #0b7d55;
}

.account-card-heading h4,
.account-card-heading p {
  margin: 0;
  text-align: left;
}

.account-card-heading h4 {
  color: #17243d;
  font-size: 0.98rem;
}

.account-card-heading p {
  margin-top: 0.2rem;
  color: #68798b;
  font-size: 0.82rem;
}

.account-form {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.8rem;
  margin-top: 1rem;
}

.password-form {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.profile-form {
  grid-template-columns: repeat(2, minmax(0, 15rem)) minmax(0, 1fr);
  align-items: end;
  width: 100%;
  margin-top: auto;
}

.profile-form .account-form-actions {
  grid-column: auto;
  justify-content: flex-end;
}

.account-form label {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  color: #435469;
  font-size: 0.78rem;
  font-weight: 700;
}

.account-form input {
  min-height: 2.35rem;
  padding: 0.45rem 0.65rem;
  box-sizing: border-box;
  border: 1px solid #d3dce6;
  border-radius: 8px;
  background: #fff;
  color: #17243d;
  font: inherit;
}

.account-form input:focus {
  border-color: #77b797;
  outline: 0;
  box-shadow: 0 0 0 3px rgba(11, 125, 85, 0.1);
}

.account-form-actions {
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  grid-column: 1 / -1;
}

.primary-action,
.secondary-action,
.danger-action {
  min-height: 2.3rem;
  padding: 0.48rem 0.9rem;
  border: 0;
  border-radius: 8px;
  font: inherit;
  font-size: 0.84rem;
  font-weight: 700;
  cursor: pointer;
}

.primary-action {
  background: #147a55;
  color: #fff;
}

.primary-action:hover:not(:disabled) {
  background: #0f6546;
}

.primary-action:disabled,
.danger-action:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.external-account-action {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin: auto 0 0 2.75rem;
}

.external-account-action p {
  margin: 0;
  color: #536477;
  font-size: 0.85rem;
}

.danger-card {
  border-color: #f0d2d7;
  background: #fffafb;
}

.danger-card .account-card-heading > i {
  background: #fdecef;
  color: #c5223d;
}

.danger-card > .danger-action {
  display: block;
  width: auto !important;
  align-self: flex-end;
  margin: auto 0 0;
}

.danger-action {
  background: #c9253f;
  color: #fff;
}

.danger-action:hover:not(:disabled) {
  background: #ab1f35;
}

.secondary-action {
  background: #e9edf1;
  color: #36475a;
}

.form-message {
  margin: 0.75rem 0 0;
  font-size: 0.82rem;
  font-weight: 700;
}

.form-message.success {
  color: #087a3e;
}

.form-message.error {
  color: #c5223d;
}

.personal-modal {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(15, 23, 42, 0.5);
}

.personal-modal-card {
  width: min(28rem, 100%);
  padding: 1.5rem;
  box-sizing: border-box;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.25);
  text-align: center;
}

.personal-modal-card h3,
.personal-modal-card p {
  margin: 0;
}

.personal-modal-card p {
  margin-top: 0.55rem;
  color: #68798b;
  line-height: 1.45;
}

.modal-icon {
  width: 3rem;
  height: 3rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.8rem;
  border-radius: 999px;
  background: #fdecef;
  color: #c5223d;
  font-size: 1.25rem;
}

.modal-actions {
  display: flex;
  justify-content: center;
  gap: 0.65rem;
  margin-top: 1.2rem;
}

.clerk-profile-modal-content {
  position: relative;
  width: min(62rem, 100%);
  max-height: min(46rem, 92vh);
  overflow: auto;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.28);
}

.clerk-profile-close {
  position: absolute;
  top: 0.75rem;
  right: 0.85rem;
  z-index: 2;
  width: 2.2rem;
  height: 2.2rem;
  border: 0;
  border-radius: 999px;
  background: #eef5f0;
  color: #0f5132;
  font-size: 1.45rem;
  cursor: pointer;
}

.clerk-profile-host {
  min-height: 34rem;
  padding: 1.5rem;
}

@media (min-width: 1101px) and (max-height: 940px) {
  .personal-page {
    --personal-navbar-reserved-height: 92px;
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
  }

  .area-page-header {
    min-height: 5.2rem;
    padding-top: 0.55rem;
    padding-bottom: 0.55rem;
  }

  .area-page-header__icon {
    width: 3.35rem;
    height: 3.35rem;
    flex-basis: 3.35rem;
  }

  .personal-sidebar {
    padding: 1rem 0.55rem;
  }

  .personal-avatar {
    width: 5.1rem;
    height: 5.1rem;
    flex-basis: 5.1rem;
    font-size: 2rem;
  }

  .personal-sidebar-hero {
    gap: 0.55rem;
    padding-top: 0.25rem;
    padding-bottom: 0.9rem;
  }

  .personal-sidebar-divider {
    margin: 0 0 0.5rem;
  }

  .personal-panel-viewport {
    padding: 0.75rem 1rem 1rem;
  }

  .overview-panel {
    padding: 0.9rem 1.05rem;
  }

  .panel-heading {
    margin-bottom: 0.7rem;
  }

  .metric-card {
    height: 5.5rem;
    min-height: 5.5rem;
    padding: 0.75rem;
  }

  .metric-card > div > span {
    font-size: 0.86rem;
  }

  .personal-libraries {
    margin-top: 0.8rem;
    padding-top: 0.8rem;
  }

  .personal-libraries-heading {
    margin-bottom: 0.65rem;
  }

  .personal-library-card {
    height: 5.5rem;
    min-height: 5.5rem;
    padding: 0.65rem 0.75rem;
  }
}

@media (min-width: 1101px) and (max-height: 900px) {
  .personal-page {
    row-gap: 0.6rem;
    padding-top: 0.6rem;
    padding-bottom: 0.6rem;
  }

  .area-page-header {
    min-height: 4.15rem;
    padding: 0.38rem 10rem 0.38rem 0.75rem;
    gap: 0.7rem;
  }

  .area-page-header__icon {
    width: 2.9rem;
    height: 2.9rem;
    flex-basis: 2.9rem;
  }

  .area-page-header__title {
    font-size: 1.2rem;
  }

  .area-page-header__subtitle {
    margin-top: 0.16rem;
    font-size: 0.74rem;
  }

  .area-page-header__visual--personal {
    right: 0.9rem;
    bottom: -1.55rem;
    width: 9rem;
  }
}

@media (max-width: 1100px) {
  .personal-page {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: auto;
    max-height: none;
    min-height: calc(100vh - 88px);
    margin-inline: 0;
    padding: 1rem clamp(1rem, 5vw, 6rem);
    overflow: auto;
  }

  .area-page-header {
    flex: 0 0 auto;
  }

  .personal-sidebar {
    flex: 0 0 auto;
    overflow: auto;
  }

  .personal-main {
    height: auto;
    overflow: visible;
  }

  .overview-panel {
    overflow: auto;
  }

  .personal-workspace,
  .personal-panel-viewport,
  .personal-panel {
    height: auto;
    min-height: 28rem;
  }

  .personal-metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .personal-library-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 980px) {
  .personal-page {
    padding-inline: 1rem;
  }
}

@media (max-width: 720px) {
  .area-page-header {
    min-height: 5.2rem;
    padding-right: 1rem;
  }

  .area-page-header__visual {
    display: none;
  }

  .personal-metrics,
  .personal-library-grid,
  .account-form,
  .password-form {
    grid-template-columns: 1fr;
  }

  .account-sections {
    grid-template-rows: none;
  }

  .account-card {
    min-height: 9.5rem;
  }

  .account-form.profile-form {
    grid-template-columns: 1fr;
  }

  .profile-form .account-form-actions {
    grid-column: 1;
  }

  .panel-heading,
  .external-account-action {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
